/**
 * Purchase-status query hook (welcome-bonus modal trigger).
 *
 * Uses the SHARED `queryKeys.offers.purchaseStatus(userId)` key so this read
 * dedupes against every other surface that fetches purchase status (the audit's
 * "getPurchaseStatus fetched twice per page" finding - OfferStore + GameLobby).
 * Keying both off the same shared cache entry means one round-trip per player,
 * shared across surfaces, with one invalidation per purchase.
 *
 * NOTE: OfferStore.jsx still fetches purchase status its own way today (a later
 * audit wave). Adopting the shared key here is forward-compatible: the moment
 * OfferStore migrates to the same key, the two fetches collapse into one with no
 * further change here.
 */

import { useQuery } from "@tanstack/react-query";
import casinoService from "../../../services/casinoService";
import { queryKeys } from "../../../shared/data/queryKeys";
import { normalizeQueryError } from "../../../shared/data/errors";

/**
 * @param {string|undefined} userId - logged-in player id (gates the query)
 */
export function usePurchaseStatus(userId) {
  return useQuery({
    queryKey: queryKeys.offers.purchaseStatus(userId),
    enabled: Boolean(userId),
    queryFn: async () => {
      try {
        return await casinoService.getPurchaseStatus();
      } catch (err) {
        throw normalizeQueryError(err, "Failed to load purchase status");
      }
    },
  });
}

export default usePurchaseStatus;
